import React from "react";
import {
  Fieldset,
  Legend,
  Field,
  Label,
  Description,
  ErrorMessage,
} from "@/components/fieldset";
import { Input } from "@/components/input";
import { Select } from "@/components/select";
import { Listbox, ListboxOption } from "@/components/listbox";
import { Button } from "@/components/button";
import type { ICreateExamPaperRequest } from "@/apis/exam_papers/types";
import { getDifficultyLabel, getSectionLabel } from "@/utils/exam";
import { useQueryGetExamConfig } from "@/apis/exam_configs/getExamConfig";
import { getPaperTypeLabel } from "@/constants/exam";

interface ExamPaperFormProps {
  initialData?: Partial<ICreateExamPaperRequest>;
  onSubmit: (data: ICreateExamPaperRequest) => void;
  onCancel?: () => void;
  isLoading?: boolean;
  serverError?: string;
  submitButtonText?: string;
  isEditMode?: boolean;
}

export const ExamPaperForm: React.FC<ExamPaperFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  serverError,
  submitButtonText = "Submit",
  isEditMode = false,
}) => {
  // Helper function to get default sections based on exam type and subject from ExamConfig
  const getDefaultSections = (
    examType: string,
    subject: string,
    config?: typeof examConfig
  ) => {
    // If examConfig is available, use it to get the default values
    if (config?.subjects) {
      const subjectConfig = config.subjects.find((s) => s.subject === subject);
      if (subjectConfig) {
        // For SAT exam type, create 2 sections with same duration and question count
        if (examType === "SAT") {
          return [
            {
              duration: subjectConfig.duration_default,
              question_count: subjectConfig.question_count_default,
            },
            {
              duration: subjectConfig.duration_default,
              question_count: subjectConfig.question_count_default,
            },
          ];
        } else {
          // For other exam types, create single section with full duration and questions
          return [
            {
              duration: subjectConfig.duration_default,
              question_count: subjectConfig.question_count_default,
            },
          ];
        }
      }
    }

    // If no config found, return sections with 0 values
    if (examType === "SAT") {
      return [
        { duration: 0, question_count: 0 },
        { duration: 0, question_count: 0 },
      ];
    } else {
      return [{ duration: 0, question_count: 0 }];
    }
  };

  const [formData, setFormData] = React.useState<ICreateExamPaperRequest>({
    exam_type: initialData?.exam_type || "SAT",
    subject: initialData?.subject || "",
    title: initialData?.title || "",
    status: initialData?.status || "active",
    difficulty: initialData?.difficulty || 0,
    is_private: initialData?.is_private ?? false, // Default to public (false means public)
    choice_count: initialData?.choice_count,
    sections: initialData?.sections || [],
  });

  const [errors, setErrors] = React.useState<Record<string, string>>({});

  // Fetch exam config based on selected exam type
  const { data: examConfig, isLoading: isLoadingConfig } =
    useQueryGetExamConfig(formData.exam_type, {
      enabled: !!formData.exam_type,
    });

  // Auto-select first subject when examConfig loads and subject is empty
  React.useEffect(() => {
    if (examConfig?.subjects?.length && !formData.subject && !isEditMode) {
      const firstSubject = examConfig.subjects[0].subject;
      setFormData((prev) => ({
        ...prev,
        subject: firstSubject,
      }));
    }
  }, [examConfig, formData.subject, isEditMode]);

  // Update sections when examConfig is loaded and we have both exam_type and subject
  React.useEffect(() => {
    if (examConfig && formData.exam_type && formData.subject) {
      const defaultSections = getDefaultSections(
        formData.exam_type,
        formData.subject,
        examConfig
      );

      // Only update if sections are empty (initial load) or if we're not in edit mode
      if (formData.sections.length === 0 || !isEditMode) {
        setFormData((prev) => ({
          ...prev,
          sections: defaultSections.map((defaultSection, index) => ({
            duration: defaultSection.duration,
            question_count: isEditMode
              ? prev.sections[index]?.question_count ||
                defaultSection.question_count
              : defaultSection.question_count,
          })),
        }));
      }
    }
  }, [
    examConfig,
    formData.exam_type,
    formData.subject,
    isEditMode,
    formData.sections.length,
  ]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors: Record<string, string> = {};

    if (!formData.title) {
      validationErrors.title = "Exam paper title is required";
    }

    if (!formData.subject) {
      validationErrors.subject = "Subject is required";
    }

    formData.sections.forEach((section, index) => {
      if (section.duration <= 0) {
        validationErrors[`section${index}-duration`] =
          "Duration must be greater than 0";
      }
      if (!isEditMode && section.question_count <= 0) {
        validationErrors[`section${index}-questions`] =
          "Number of questions must be greater than 0";
      }
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    onSubmit(formData);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (name === "exam_type") {
      // When exam type changes, reset subject so auto-select picks the first from new config
      setFormData((prev) => ({
        ...prev,
        exam_type: value,
        subject: "",
        sections: [],
      }));
    } else if (name === "subject") {
      // When subject changes, update section durations and question counts with default values from ExamConfig
      const defaultSections = getDefaultSections(
        formData.exam_type,
        value,
        examConfig
      );

      setFormData((prev) => ({
        ...prev,
        subject: value,
        sections: defaultSections.map((defaultSection, index) => ({
          duration: defaultSection.duration,
          question_count: isEditMode
            ? prev.sections[index]?.question_count || 0
            : defaultSection.question_count,
        })),
      }));
    } else if (type === "number") {
      setFormData((prev) => ({
        ...prev,
        [name]: value ? parseInt(value, 10) : 0,
      }));
    } else if (name === "difficulty") {
      setFormData((prev) => ({
        ...prev,
        difficulty: parseInt(value, 10),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Clear errors for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSectionChange = (
    sectionIndex: number,
    field: string,
    value: any
  ) => {
    setFormData((prev) => ({
      ...prev,
      sections: prev.sections.map((section, index) =>
        index === sectionIndex
          ? {
              ...section,
              [field]:
                field === "duration" || field === "question_count"
                  ? parseInt(value, 10)
                  : value,
            }
          : section
      ),
    }));

    // Clear section errors
    const errorKey = `section${sectionIndex}-${field}`;
    if (errors[errorKey]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  // Handle access level change for ListBox
  const handleAccessLevelChange = (value: boolean) => {
    setFormData((prev) => ({
      ...prev,
      is_private: value,
    }));
  };

  // Get available subjects from exam config
  const getAvailableSubjects = () => {
    if (!examConfig?.subjects) return [];
    return examConfig.subjects;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Server error display */}
      {serverError && (
        <div className="p-4 bg-red-50 text-red-600 rounded-md">
          {serverError}
        </div>
      )}

      {/* Basic Information */}
      <Fieldset>
        <Legend>Basic Information</Legend>
        <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2">
          <Field>
            <Label htmlFor="title">Exam Paper Title*</Label>
            <Input
              id="title"
              name="title"
              placeholder="Enter exam paper title"
              value={formData.title}
              onChange={handleChange}
              aria-invalid={!!errors.title}
            />
            {errors.title && <ErrorMessage>{errors.title}</ErrorMessage>}
          </Field>

          <Field>
            <Label htmlFor="exam_type">Exam Type*</Label>
            <Select
              id="exam_type"
              name="exam_type"
              value={formData.exam_type}
              onChange={handleChange}
              disabled={isEditMode}
              required
            >
              <option value="SAT">SAT</option>
              <option value="ACT">ACT</option>
              <option value="AP">AP</option>
            </Select>
            {isEditMode && (
              <Description>
                Exam type cannot be changed after creation.
              </Description>
            )}
          </Field>

          <Field>
            <Label htmlFor="subject">Subject*</Label>
            <Select
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              disabled={isEditMode || isLoadingConfig}
              required
            >
              <option value="">
                {isLoadingConfig ? "Loading subjects..." : "Select a subject"}
              </option>
              {getAvailableSubjects().map((subjectConfig) => (
                <option
                  key={subjectConfig.subject}
                  value={subjectConfig.subject}
                >
                  {getPaperTypeLabel(subjectConfig.subject)}
                </option>
              ))}
            </Select>
            {isEditMode && (
              <Description>
                Subject cannot be changed after creation.
              </Description>
            )}
            {errors.subject && <ErrorMessage>{errors.subject}</ErrorMessage>}
          </Field>

          <Field>
            <Label htmlFor="difficulty">Difficulty</Label>
            <Select
              id="difficulty"
              name="difficulty"
              value={formData.difficulty?.toString() || "0"}
              onChange={handleChange}
            >
              <option value="0">Not Selected</option>
              {[1, 2, 3, 4, 5].map((level) => (
                <option key={level} value={level.toString()}>
                  {getDifficultyLabel(level)}
                </option>
              ))}
            </Select>
            <Description>
              Select the difficulty level for this exam paper.
            </Description>
          </Field>

          <Field>
            <Label>Student Access</Label>
            <Listbox 
              value={formData.is_private}
              onChange={handleAccessLevelChange}
            >
              <ListboxOption value={false}>
                Public - Students can review questions after exam
              </ListboxOption>
              <ListboxOption value={true}>
                Private - Questions not accessible to students
              </ListboxOption>
            </Listbox>
            <Description>
              Public access allows students to review questions after completing the exam.
            </Description>
          </Field>
        </div>
      </Fieldset>

      {/* Section Settings */}
      {formData.exam_type && formData.subject ? (
        <Fieldset>
          <Legend>Section Settings</Legend>
          <div className="mt-2 text-sm text-zinc-500">
            Configure the duration and questions for each section. Default
            values are automatically loaded from the exam configuration.
          </div>
          <div className="mt-6 space-y-6">
            {formData.sections.map((section, sectionIndex) => (
              <div
                key={sectionIndex}
                className="p-4 border border-gray-200 rounded-md bg-gray-50"
              >
                {formData.sections.length > 1 && (
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {getSectionLabel(formData.exam_type)} {sectionIndex + 1}
                  </h3>
                )}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field>
                    <Label htmlFor={`section${sectionIndex}-duration`}>
                      Duration (minutes)*
                    </Label>
                    <Input
                      id={`section${sectionIndex}-duration`}
                      type="number"
                      min="1"
                      max="240"
                      value={section.duration}
                      onChange={(e) =>
                        handleSectionChange(
                          sectionIndex,
                          "duration",
                          e.target.value
                        )
                      }
                      aria-invalid={!!errors[`section${sectionIndex}-duration`]}
                    />
                    {errors[`section${sectionIndex}-duration`] && (
                      <ErrorMessage>
                        {errors[`section${sectionIndex}-duration`]}
                      </ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label htmlFor={`section${sectionIndex}-questions`}>
                      Number of Questions*
                    </Label>
                    <Input
                      id={`section${sectionIndex}-questions`}
                      type="number"
                      min="1"
                      max="100"
                      value={section.question_count}
                      onChange={(e) =>
                        handleSectionChange(
                          sectionIndex,
                          "question_count",
                          e.target.value
                        )
                      }
                      disabled={isEditMode}
                      aria-invalid={
                        !!errors[`section${sectionIndex}-questions`]
                      }
                    />
                    {errors[`section${sectionIndex}-questions`] && (
                      <ErrorMessage>
                        {errors[`section${sectionIndex}-questions`]}
                      </ErrorMessage>
                    )}
                  </Field>
                </div>
              </div>
            ))}
          </div>

          {/* Choice Count Override */}
          <div className="mt-6 max-w-xs">
            <Field>
              <Label htmlFor="choice_count">객관식 유형</Label>
              <Select
                id="choice_count"
                name="choice_count"
                value={String(formData.choice_count || (examConfig?.subjects?.find(
                  (s) => s.subject === formData.subject
                )?.choice_count ?? 4))}
                onChange={(e) => {
                  const newValue = parseInt(e.target.value, 10);
                  const currentValue = formData.choice_count || (examConfig?.subjects?.find(
                    (s) => s.subject === formData.subject
                  )?.choice_count ?? 4);
                  if (newValue < currentValue) {
                    if (!window.confirm("선택지 개수를 줄이면 기존 문제의 초과 선택지 데이터가 삭제될 수 있습니다. 계속하시겠습니까?")) {
                      return;
                    }
                  }
                  setFormData((prev) => ({
                    ...prev,
                    choice_count: newValue,
                  }));
                }}
              >
                <option value="4">4지선다</option>
                <option value="5">5지선다</option>
              </Select>
            </Field>
          </div>
        </Fieldset>
      ) : (
        <Fieldset>
          <Legend>Section Settings</Legend>
          <div className="mt-4 p-6 bg-gray-50 border border-gray-200 rounded-md text-center">
            <div className="text-gray-500">
              Please select an Exam Type and Subject to configure section
              settings.
            </div>
          </div>
        </Fieldset>
      )}

      {/* Form Actions */}
      <div className="flex justify-end space-x-4">
        {onCancel && (
          <Button type="button" plain onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={isLoading}>
          {isLoading ? "Submitting..." : submitButtonText}
        </Button>
      </div>
    </form>
  );
};
